module.exports = {
  title: 'How Much Office Space Does Your Company Need? ',
  callToActionTitle: 'Find out how much space your company needs.',
  pageTitle: 'How Much Office Space Does Your Company Need? | CBRE Spacer',
  metaTagText: 'Having the appropriate office space can impact employee satisfaction, productivity, growth, culture, and company success. Learn more here.',
  preview: `
    If you’re planning an office move or looking to right-size your current space, 
    you will need to answer one question before anything else: how much office space 
    does your company need?.
  `,
  content: `
    <p>
    If you’re planning an office move or looking to right-size your current space, 
    you will need to answer one question before anything else: how much office space 
    does your company need? 
    </p>
    <p>
    It sounds straightforward. It isn’t — and getting it wrong is expensive in both directions. 
    Too much space means paying for square footage your team doesn’t use. Too little creates a crowded 
    environment that drives people to work elsewhere. 
    </P>
    <h2>
    Why headcount alone isn’t enough 
    </h2>
    <p>
    The most common starting point is a square footage formula: multiply the number of employees 
    by a target square feet per person. The typical benchmark is 100–150 sq ft per person, but that 
    figure tells you very little on its own. 
    </P>
    <p>
    The right amount of space depends on how your team actually works. A company where most people 
    are in the office every day has different needs than one running a hybrid schedule, where peak 
    occupancy might be 60–70% of total headcount on any given day. A team that collaborates constantly 
    needs a different mix of space than one doing mostly focused individual work. 
    </P>
    <p>
    Every decision you make about your office environment shapes how much space you need. 
    Adding an on-site gym or wellness area increases your footprint. Introducing desk-sharing 
    or unassigned seating reduces it. Your culture, your growth plans and even your brand 
    all play a role. 
    </P>
    <h2>
    What ‘the right amount of space’ actually means 
    </h2>
    <p>
    The right office size isn’t a single number — it’s a combination of factors:
    </P>
    <ul class="has-margin-left-6 has-margin-bottom-5" style="list-style: disc">
      <li class="has-margin-left-6">
      How many individual workstations you need, based on attendance patterns and your seating model 
      </li>
      <li class="has-margin-left-6">
      How much meeting and collaboration space you need, based on how your team works together 
      </li>
      <li class="has-margin-left-6">
      What amenity, support and circulation space you need to make the office functional and pleasant to work in 
      </li>
    </ul>
    <p>
    When you add these up with the right ratios and benchmarks, you get a total office size that 
    actually reflects your organization — not just your headcount. 
    </P>
    <h2>
    How to figure out what you need 
    </h2>
    <p>
    In the past, getting a properly calibrated space recommendation meant commissioning a Workplace 
    Strategy engagement — a process involving utilization studies, staff interviews and 
    visioning sessions. That’s still the right approach for large or complex organizations. 
    </P>
    <p>
    For most companies, CBRE’s free office space calculator gives you the same rigor in 15 minutes. 
    Answer a few questions about your team, your workstyle and your priorities. 
    The tool applies CBRE’s workplace benchmarks and gives you a recommended office size, 
    a full space breakdown and the ratios behind each figure. 
    </P>
    <p>
    You can use the results immediately — share them with your broker to shortlist properties, 
    or hand them to your architect to start planning your fit-out. 
    </P>
    <h2>
    What happens if you get it wrong? 
    </h2>
    <p>
    Offices are typically leased for five years or more. An office that’s the wrong size from 
    day one creates friction that compounds over time — either in wasted cost or in a working 
    environment that doesn’t support your team. 
    </P>
    <p>
    Spending 15 minutes to get the right number at the start of your search is one of the 
    highest-value things you can do before committing to a space. 
    </P>
  `
};
